import React, { useMemo } from 'react';

import { Button, Popconfirm } from 'antd';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  BaseUser,
  conversationsApi,
  GroupConversationDetails,
  selectCurrentUser,
  selectIsAuthenticatedUserAdmin,
  useAppSelector,
  userApi,
} from 'core/lib';

import ModalMembersList from './modalMembersList';

type ModalMembersProps = {
  createdById: number;
  participants: BaseUser[];
  conversationDetails: GroupConversationDetails;
};

const ModalGroupMembers = ({ conversationDetails, participants }: ModalMembersProps) => {
  const { t } = useTranslation();

  const { data: users = [], isFetching: loadingUsers } = userApi.useGetUsersShortQuery();
  const currentUser = useAppSelector(selectCurrentUser);
  const isCurrentUserAdmin = useAppSelector(selectIsAuthenticatedUserAdmin);
  const [removeUserFromConversation, { isLoading: isRemovingUserLoading, originalArgs: originalRemoveUserArgs }] =
    conversationsApi.useRemoveUserFromGroupConversationMutation();

  const [addUserToConversation, { isLoading: isAddUserLoading, originalArgs: originalAddUserArgs }] =
    conversationsApi.useAddUserToGroupConversationMutation();

  const participantsIds = useMemo(() => participants.map((user) => user.id), [participants]);

  const isPartOfConversation = useMemo(
    () => currentUser?.id && participantsIds.includes(currentUser.id),
    [currentUser, participantsIds]
  );

  const onRemoveUser = (userId: number) => {
    removeUserFromConversation({
      userId,
      conversationId: conversationDetails.conversationId,
      groupConversationId: conversationDetails.id,
    });
  };

  const onAddUser = (userId: number) => {
    addUserToConversation({
      userId,
      conversationId: conversationDetails.conversationId,
      groupConversationId: conversationDetails.id,
    });
  };

  const onConfirmRemove = (userId: number) => {
    onRemoveUser(userId);
  };

  const isOwner = useMemo(
    () => conversationDetails.createdBy?.id === currentUser?.id,
    [conversationDetails.createdBy, currentUser]
  );

  const orderedUsersByParticipants = useMemo(() => {
    if (isPartOfConversation) {
      const excludeCurrentUserData = users.filter((user) => user.id !== currentUser?.id);

      return sortBy(excludeCurrentUserData, (user) => !participantsIds.includes(user.id));
    }
    return users.filter((user) => participantsIds.includes(user.id));
  }, [users, participantsIds, currentUser?.id, isPartOfConversation]);

  const getActions = (user: BaseUser) => {
    if (!isPartOfConversation && !isCurrentUserAdmin) {
      return [];
    }
    const actions = [];
    if (participantsIds.includes(user.id) && isOwner) {
      actions.push(
        <Popconfirm
          overlayClassName="max-w-80"
          title={t('conversations:modal.info.removeFromGroup', { user: user.name })}
          okText={t('actions:global.remove')}
          onConfirm={() => onConfirmRemove(user.id)}
          okButtonProps={{ danger: true }}
        >
          <Button type="link" loading={isRemovingUserLoading && originalRemoveUserArgs?.userId === user.id}>
            {t('actions:global.remove')}
          </Button>
        </Popconfirm>
      );
    } else if (!participantsIds.includes(user.id)) {
      actions.push(
        <Button
          type="link"
          loading={isAddUserLoading && originalAddUserArgs?.userId === user.id}
          onClick={() => onAddUser(user.id)}
        >
          {t('actions:global.add')}
        </Button>
      );
    }

    return actions;
  };

  return <ModalMembersList getActions={getActions} users={orderedUsersByParticipants} isLoading={loadingUsers} />;
};

export default ModalGroupMembers;
